import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Book, ArrowLeft, Calendar, User, Clock, Play, Pause, Volume2, Loader2, Music, Grid3X3, BookOpen, Edit2, Save, X, Check, Library, LayoutGrid, Heart, Filter, SlidersHorizontal, Share2, ListPlus, Plus } from 'lucide-react';
import { TeddyBear } from '@/components/icons/TeddyBear';
import { Link } from 'wouter';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { Story } from '@shared/schema';
import { AudioLoadingAnimation } from '@/components/AudioLoadingAnimation';
import { AnimatedBookshelf } from '@/components/AnimatedBookshelf';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { detectStoryMoments, shouldTriggerSound, type StoryMoment } from '@/utils/storyMomentDetector';
import { useToast } from '@/hooks/use-toast';
import { MerchandiseCatalog } from '@/components/MerchandiseCatalog';
import { StickerGenerator } from '@/components/StickerGenerator';
import { ShareStory } from '@/components/ShareStory';
import { PrintBookOrder } from '@/components/PrintBookOrder';
import { PdfBookDownload } from '@/components/PdfBookDownload';
import { CommunityShareButton } from '@/components/CommunityShareButton';
import { VoiceAssignmentDialog } from '@/components/VoiceAssignmentDialog';
import { SegmentedAudioPlayer } from '@/components/SegmentedAudioPlayer';
import { useAuth } from '@/hooks/useAuth';
import { useGender } from '@/contexts/GenderContext';
import { getPersonalizedAppName } from '@/lib/appName';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StorySearch } from '@/components/StorySearch';
import { useQueryParams } from '@/hooks/useQueryParams';
import Navigation from '@/components/Navigation';
import Footer from '@/components/ui/footer';
import StoryLibraryErrorBoundary from '@/components/StoryLibraryErrorBoundary';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

export default function Stories() {
  const { user } = useAuth();
  const { selectedGender } = useGender();
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const { toast } = useToast();
  
  // Playlist dialog state
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [selectedStoryForPlaylist, setSelectedStoryForPlaylist] = useState<Story | null>(null);
  
  // Voice assignment dialog state
  const [voiceAssignmentOpen, setVoiceAssignmentOpen] = useState(false);
  const [selectedStoryForVoice, setSelectedStoryForVoice] = useState<Story | null>(null);
  
  // Get gender-based logo
  const getLogoSrc = () => {
    switch (selectedGender) {
      case 'girl':
        return '/logo-pink.png';
      case 'boy':
        return '/logo-blue.png';
      case 'other':
        return '/logo-purple.png';
      default:
        return '/logo.png';
    }
  };
  
  // Query params hook for state persistence
  const { params, updateParams, getSearchFilters } = useQueryParams();
  
  // Search and filter states - now derived from URL params
  const [viewMode, setViewMode] = useState<'grid' | 'bookshelf'>(params.view || 'bookshelf');
  const [searchFilters, setSearchFilters] = useState(getSearchFilters());
  const [filterFavorites, setFilterFavorites] = useState(false); // Always start with favorites filter off
  
  // Update local state when URL params change
  useEffect(() => {
    setViewMode(params.view || 'bookshelf');
    setFilterFavorites(params.favorites || false);
    
    // Update search filters based on params
    const filters: { query?: string; age?: string; style?: string; language?: string } = {};
    if (params.query && params.query.trim()) {
      filters.query = params.query;
    }
    if (params.age && params.age !== 'all') {
      filters.age = params.age;
    }
    if (params.style && params.style !== 'all') {
      filters.style = params.style;
    }
    if (params.language && params.language !== 'all') {
      filters.language = params.language;
    }
    setSearchFilters(filters);
  }, [params]);
  
  // Initialize sound effects
  const soundEffects = useSoundEffects();
  
  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioText, setAudioText] = useState<string>(''); // Store the actual text used for audio
  const [highlightingEnabled, setHighlightingEnabled] = useState(true); // Toggle for word highlighting
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(true); // Toggle for sound effects
  const [storyMoments, setStoryMoments] = useState<StoryMoment[]>([]); // Detected story moments
  const [lastSoundIndex, setLastSoundIndex] = useState(-10); // Track last sound effect to avoid overlaps

  // Fetch stories - either all or search results
  const { data: stories, isLoading, isFetching } = useQuery({
    queryKey: ['/api/stories', searchFilters.query || '', searchFilters.age || '', searchFilters.style || '', searchFilters.language || ''],
    queryFn: async () => {
      // If search filters are provided, use search endpoint
      if (Object.keys(searchFilters).length > 0) {
        const params = new URLSearchParams();
        if (searchFilters.query) params.append('query', searchFilters.query);
        if (searchFilters.age) params.append('age', searchFilters.age);
        if (searchFilters.style) params.append('style', searchFilters.style);
        if (searchFilters.language) params.append('language', searchFilters.language);
        
        const response = await apiRequest('GET', `/api/stories/search?${params.toString()}`);
        const data = await response.json();
        return (data.stories || []) as Story[];
      } else {
        // Otherwise, get all stories
        const response = await apiRequest('GET', '/api/stories');
        const data = await response.json();
        return (data.stories || []) as Story[];
      }
    },
    placeholderData: (prev) => prev
  });
  
  // Handle search filter changes with URL persistence
  const handleSearchFiltersChange = (newFilters: {
    query?: string;
    age?: string;
    style?: string;
    language?: string;
  }) => {
    setSearchFilters(newFilters);
    updateParams(newFilters);
  };
  
  // Handle view mode changes with URL persistence
  const handleViewModeChange = (newViewMode: 'grid' | 'bookshelf') => {
    setViewMode(newViewMode);
    updateParams({ view: newViewMode });
  };
  
  // Handle favorites toggle with URL persistence
  const handleFavoritesToggle = () => {
    const newFavoritesState = !filterFavorites;
    setFilterFavorites(newFavoritesState);
    updateParams({ favorites: newFavoritesState });
  };

  // Fetch user's favorites
  const { data: favorites } = useQuery({
    queryKey: ['/api/favorites'],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/favorites');
      const data = await response.json();
      return data;
    },
    placeholderData: (prev) => prev
  });

  const favoriteIds = favorites?.favoriteIds || [];

  // Fetch user's playlists
  const { data: playlists } = useQuery({
    queryKey: ['/api/playlists'],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/playlists');
      const data = await response.json();
      return data.playlists || [];
    },
    placeholderData: (prev) => prev
  });

  // Filter stories based on favorites
  const filteredStories = stories?.filter(story => {
    if (filterFavorites && !favoriteIds.includes(story.id)) return false;
    return true;
  }) || [];

  // Get unique ages and styles from stories for filter options
  const uniqueAges = Array.from(new Set(stories?.map(s => s.age) || [])).sort((a, b) => a - b);
  const uniqueStyles = Array.from(new Set(stories?.map(s => s.style) || []));

  // Mutation for toggling favorites
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (storyId: number) => {
      const response = await apiRequest('POST', `/api/stories/${storyId}/favorite`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favorites'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update favorite",
        variant: "destructive",
      });
    }
  });

  // Mutation for updating story title
  const updateTitleMutation = useMutation({
    mutationFn: async ({ storyId, title }: { storyId: number; title: string }) => {
      const response = await fetch(`/api/stories/${storyId}/title`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title })
      });
      if (!response.ok) {
        throw new Error('Failed to update title');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/stories'] });
      // Update the selected story if it's the one being edited
      if (selectedStory && selectedStory.id === variables.storyId) {
        setSelectedStory({ ...selectedStory, title: variables.title });
      }
      toast({
        title: "Title updated",
        description: "Story title has been successfully updated.",
      });
      setEditingTitleId(null);
      setEditingTitle('');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update title. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Mutation for creating a new playlist
  const createPlaylistMutation = useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/playlists', { name, description });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/playlists'] });
      toast({
        title: "Playlist created",
        description: "Your new playlist has been created successfully.",
      });
      setCreatePlaylistOpen(false);
      setNewPlaylistName('');
      setNewPlaylistDescription('');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create playlist",
        variant: "destructive",
      });
    }
  });

  // Mutation for adding a story to a playlist
  const addToPlaylistMutation = useMutation({
    mutationFn: async ({ playlistId, storyId }: { playlistId: number; storyId: number }) => {
      const response = await apiRequest('POST', `/api/playlists/${playlistId}/stories`, { storyId });
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/playlists'] });
      queryClient.invalidateQueries({ queryKey: ['/api/playlists', variables.playlistId] });
      toast({
        title: "Story added",
        description: "Story has been added to playlist successfully.",
      });
      setSelectedStoryForPlaylist(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add story to playlist",
        variant: "destructive",
      });
    }
  });

  // Helper function to clean story content for audio (remove image paths)
  const cleanTextForAudio = (text: string) => {
    // Remove image paths in both formats: ![alt](path) and (/objects/uploads/...)
    return text
      .replace(/\!\[.*?\]\([^)]*\)/g, '') // Remove markdown images
      .replace(/\([\/]?objects\/uploads\/[^)]+\)/g, '') // Remove path-only images
      .replace(/\s+/g, ' ') // Clean up multiple spaces
      .trim();
  };

  // Generate audio from the story text
  const generateAudio = async (storyContent: string) => {
    if (!storyContent || isLoadingAudio) return;
    
    setIsLoadingAudio(true);
    console.log('Generating audio for story:', storyContent.length, 'characters');
    
    try {
      // Clean the story content for audio generation (remove image paths)
      const cleanedContent = cleanTextForAudio(storyContent);
      
      // Store the cleaned text for audio tracking
      setAudioText(cleanedContent); 
      
      const response = await fetch('/api/generate-audio', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies for authentication
        body: JSON.stringify({
          text: cleanedContent,
          voice: 'nova',
          speed: 0.9,
          storyId: selectedStory?.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to generate audio');
      }
      
      const audioBlob = await response.blob();
      console.log('Audio generated successfully:', audioBlob.size, 'bytes');
      const url = URL.createObjectURL(audioBlob);
      
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      setAudioUrl(url);
      
      const audio = new Audio(url);
      audioRef.current = audio;
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentWordIndex(-1);
      });
      
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
      });
      
      // Track playback progress for word highlighting with character-based estimation
      audio.addEventListener('timeupdate', () => {
        if (!highlightingEnabled) {
          setCurrentWordIndex(-1);
          return;
        }
        
        const currentAudioText = audioText || storyContent; // Fallback to storyContent if audioText not set
        if (audio.duration > 0 && currentAudioText) {
          const progress = audio.currentTime / audio.duration;
          const words = currentAudioText.split(/\s+/);
          
          // Calculate cumulative character positions with adjustment for speech patterns
          let totalChars = currentAudioText.length;
          // Apply a slight adjustment to account for natural speech pauses
          const adjustedProgress = Math.min(progress * 1.02, 1); // Slight speed-up to compensate for pauses
          let currentChars = Math.floor(adjustedProgress * totalChars);
          
          let charCount = 0;
          let wordIndex = -1;
          
          // Find the current word based on character position
          for (let i = 0; i < words.length; i++) {
            if (charCount <= currentChars && currentChars < charCount + words[i].length + 1) {
              wordIndex = i;
              break;
            }
            charCount += words[i].length + 1; // +1 for space
          }
          
          // Ensure we highlight the last word when near the end
          if (progress > 0.98 && wordIndex === -1) {
            wordIndex = words.length - 1;
          }
          
          setCurrentWordIndex(wordIndex);
        }
      });
      
      // Add error handler for playback
      audio.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        const target = e.target as HTMLAudioElement;
        console.error('Audio error details:', {
          error: target.error,
          src: target.src,
          readyState: target.readyState,
          networkState: target.networkState
        });
        alert('There was an error playing the audio. Please try again.');
        setIsPlaying(false);
        setIsLoadingAudio(false);
      });
      
      // Log audio metadata when loaded
      audio.addEventListener('loadeddata', () => {
        console.log('Audio loaded:', {
          duration: audio.duration,
          src: audio.src.substring(0, 50) + '...',
          textLength: storyContent.length
        });
      });
      
      // Don't autoplay - wait for user to click play button
      // This avoids browser autoplay restrictions
      setIsPlaying(false);
      console.log('Audio ready to play. Click the play button to start.');
    } catch (error) {
      console.error('Error generating audio:', error);
      let errorMessage = 'Sorry, I couldn\'t create the audio for this story.';
      
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        if (error.message.includes('timeout')) {
          errorMessage = 'The story is taking longer than expected to convert to audio. Please try again with a shorter story.';
        } else if (error.message.includes('Failed to generate audio')) {
          errorMessage = 'There was an issue with the audio service. Please try again in a moment.';
        } else if (error.message.includes('API')) {
          errorMessage = 'There was an issue connecting to the audio service. Please check your internet connection and try again.';
        }
      }
      
      toast({
        title: "Audio Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!selectedStory) return;
    
    if (!audioRef.current) {
      generateAudio(selectedStory.content);
      return;
    }
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Play with error handling
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((error) => {
        console.error('Error playing audio:', error);
        // Don't show alert - user can just click again
        setIsPlaying(false);
      });
      
      // Resume word highlighting from current position with character-based estimation
      const currentAudioText = audioText || selectedStory.content; // Use the same text used for audio
      if (highlightingEnabled && audioRef.current.duration > 0 && currentAudioText) {
        const progress = audioRef.current.currentTime / audioRef.current.duration;
        const words = currentAudioText.split(/\s+/);
        
        // Calculate cumulative character positions with adjustment for speech patterns
        let totalChars = currentAudioText.length;
        // Apply adjustment to sync with speech timing - increased for better accuracy
        const adjustedProgress = Math.min(progress * 1.05, 1); // 5% speed-up to compensate for pauses and speech timing
        let currentChars = Math.floor(adjustedProgress * totalChars);
        
        let charCount = 0;
        let wordIndex = -1;
        
        // Find the current word based on character position
        for (let i = 0; i < words.length; i++) {
          if (charCount <= currentChars && currentChars < charCount + words[i].length + 1) {
            wordIndex = i;
            break;
          }
          charCount += words[i].length + 1; // +1 for space
        }
        
        // Ensure we highlight the last word when near the end
        if (progress > 0.98 && wordIndex === -1) {
          wordIndex = words.length - 1;
        }
        
        setCurrentWordIndex(wordIndex);
        
        // Check for sound effects triggers
        if (soundEffectsEnabled && wordIndex >= 0) {
          const moment = shouldTriggerSound(storyMoments, wordIndex, lastSoundIndex);
          if (moment) {
            setLastSoundIndex(wordIndex);
            // Trigger appropriate sound effect
            switch (moment.type) {
              case 'magic':
                soundEffects.playMagicSparkle();
                break;
              case 'adventure':
                soundEffects.playWhoosh();
                break;
              case 'happy':
                soundEffects.playHappyChime();
                break;
              case 'funny':
                soundEffects.playBoing();
                break;
              case 'sleepy':
                soundEffects.playSleepyTwinkle();
                break;
            }
          }
        }
      }
    }
  };

  // Create word mapping between cleaned audio and original display text
  const createWordMapping = useCallback((content: string) => {
    if (!audioText) return [];
    
    const cleanedWords = audioText.split(/\s+/).filter(w => w.length > 0);
    const originalParts = content.split(/(\!\[.*?\]\(.*?\)|\([\/]?objects\/uploads\/[^)]+\))/);
    const mapping = [];
    let displayWordIndex = 0;
    
    // Build mapping of audio word index to display word index
    let audioWordIndex = 0;
    
    for (const part of originalParts) {
      // Skip image parts
      if (part.match(/\!\[(.*?)\]\((.*?)\)/) || part.match(/\(([\/]?objects\/uploads\/[^)]+)\)/)) {
        continue;
      }
      
      // Process text parts
      const words = part.trim().split(/\s+/).filter(w => w.length > 0);
      for (const word of words) {
        if (audioWordIndex < cleanedWords.length) {
          mapping[audioWordIndex] = displayWordIndex;
          audioWordIndex++;
        }
        displayWordIndex++;
      }
    }
    
    return mapping;
  }, [audioText]);

  const wordMapping = useMemo(() => createWordMapping(selectedStory?.content || ''), [createWordMapping, selectedStory?.content]);

  // Auto-scroll to highlighted word
  useEffect(() => {
    if (currentWordIndex >= 0 && audioText && selectedStory) {
      const displayIndex = wordMapping[currentWordIndex];
      if (displayIndex !== undefined) {
        const wordElement = document.querySelector(`[data-word-index="${displayIndex}"]`);
        if (wordElement) {
          wordElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }
    }
  }, [currentWordIndex, audioText, selectedStory, wordMapping]);

  // Render story with highlighted words and images
  const renderHighlightedStory = (content: string) => {
    // Always use original content for parsing (to show images)
    // Use proper word mapping for audio synchronization
    const parts = content.split(/(\!\[.*?\]\(.*?\)|\([\/]?objects\/uploads\/[^)]+\))/);
    let displayWordCounter = 0;
    
    return (
      <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
        {parts.map((part, partIndex) => {
          // Check if this part is a standard markdown image
          const imageMatch = part.match(/\!\[(.*?)\]\((.*?)\)/);
          if (imageMatch) {
            return (
              <img
                key={partIndex}
                src={imageMatch[2]}
                alt={imageMatch[1]}
                className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg block"
              />
            );
          }
          
          // Check if this part is a path-only image format used in stories
          const pathMatch = part.match(/\(([\/]?objects\/uploads\/[^)]+)\)/);
          if (pathMatch) {
            return (
              <img
                key={partIndex}
                src={pathMatch[1]}
                alt="Story illustration"
                className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg block"
              />
            );
          }
          
          // Handle text content with word highlighting
          const words = part.trim().split(/\s+/).filter(word => word.length > 0);
          return (
            <p key={partIndex} className="text-lg leading-relaxed text-primary mb-4" style={{ textAlign: 'justify' }}>
              {words.map((word, wordIndex) => {
                const globalDisplayWordIndex = displayWordCounter + wordIndex;
                
                // Use word mapping to find if this display word corresponds to current audio word
                let isCurrentWord = false;
                if (audioText && wordMapping.length > 0) {
                  // Find if current audio word index maps to this display word index
                  isCurrentWord = wordMapping[currentWordIndex] === globalDisplayWordIndex;
                } else {
                  isCurrentWord = globalDisplayWordIndex === currentWordIndex;
                }
                
                displayWordCounter++;
                
                return (
                  <span
                    key={wordIndex}
                    data-word-index={globalDisplayWordIndex}
                    className={`transition-all duration-300 ease-in-out ${
                      isCurrentWord
                        ? 'bg-purple-100 text-purple-900 px-0.5 rounded-sm border-b-2 border-purple-400'
                        : globalDisplayWordIndex < (wordMapping[currentWordIndex] || currentWordIndex)
                        ? 'text-gray-700 opacity-90'
                        : 'text-gray-800'
                    }`}
                  >
                    {word}{' '}
                  </span>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };

  // Cleanup audio when story changes or unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
      setAudioUrl(null);
    };
  }, [selectedStory]);

  if (selectedStory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50">
        <Navigation />
        <div className="p-4">
        <StoryLibraryErrorBoundary>
          <div className="max-w-4xl mx-auto">
          <button
            onClick={() => {
              setSelectedStory(null);
              setCurrentWordIndex(-1);
              setStoryMoments([]);
              setLastSoundIndex(-10);
              if (audioRef.current) {
                audioRef.current.pause();
                setIsPlaying(false);
              }
              if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
              }
            }}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-primary font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Stories
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-6">
              {/* Story Title */}
              {selectedStory.title && (
                <div className="text-center mb-4">
                  {editingTitleId === selectedStory.id ? (
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className="text-xl font-semibold text-primary text-center px-3 py-2 border-2 border-secondary rounded-lg focus:outline-none focus:border-primary w-full max-w-lg"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateTitleMutation.mutate({ storyId: selectedStory.id, title: editingTitle });
                          } else if (e.key === 'Escape') {
                            setEditingTitleId(null);
                            setEditingTitle('');
                          }
                        }}
                      />
                      <button
                        onClick={() => updateTitleMutation.mutate({ storyId: selectedStory.id, title: editingTitle })}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        disabled={updateTitleMutation.isPending}
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingTitleId(null);
                          setEditingTitle('');
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <h1 className="text-3xl font-bold text-primary text-center flex items-center justify-center gap-3 group">
                      <Book className="w-8 h-8 text-secondary" />
                      {selectedStory.title}
                      <button
                        onClick={() => {
                          setEditingTitleId(selectedStory.id);
                          setEditingTitle(selectedStory.title || '');
                        }}
                        className="p-1.5 text-secondary hover:text-primary hover:bg-gray-100 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                        title="Edit title"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={async () => {
                          if (navigator.share) {
                            try {
                              await navigator.share({
                                title: selectedStory.title || 'My Bedtime Story',
                                text: `Check out this amazing bedtime story: ${selectedStory.title || 'My Story'}`,
                                url: `${window.location.origin}/story/${selectedStory.id}`
                              });
                            } catch (err) {
                              console.log('Share cancelled or failed:', err);
                            }
                          } else {
                            // Fallback to copying URL
                            navigator.clipboard.writeText(`${window.location.origin}/story/${selectedStory.id}`);
                            toast({
                              title: "Link copied!",
                              description: "Story link has been copied to your clipboard",
                            });
                          }
                        }}
                        className="p-1.5 text-secondary hover:text-primary hover:bg-gray-100 rounded-lg transition-all"
                        title="Share story"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>
                    </h1>
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-center gap-4 text-sm text-secondary">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(selectedStory.createdAt).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Age {selectedStory.age}
                </span>
                <span className="capitalize px-2 py-1 bg-accent/10 text-secondary rounded-full text-xs">
                  {selectedStory.style}
                </span>
                {/* Favorite Button */}
                <button
                  onClick={() => toggleFavoriteMutation.mutate(selectedStory.id)}
                  className="flex items-center gap-1 px-3 py-1 rounded-full transition-colors hover:bg-gray-100"
                  title={favoriteIds.includes(selectedStory.id) ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart 
                    className={`w-4 h-4 ${favoriteIds.includes(selectedStory.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                  />
                  <span className="text-xs">
                    {favoriteIds.includes(selectedStory.id) ? 'Favorited' : 'Favorite'}
                  </span>
                </button>
              </div>
            </div>

            {/* Audio Player Controls */}
            <div className="mb-6 space-y-4 relative z-50">
              <div className="flex justify-center">
                <button
                  onClick={togglePlayPause}
                  disabled={isLoadingAudio}
                  className={`flex items-center gap-3 px-6 py-3 rounded-full font-medium transition-all duration-200 transform hover:scale-105 ${
                    isLoadingAudio 
                      ? 'bg-gray-200 text-gray-700 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 hover:from-purple-700 hover:to-teal-700 shadow-lg focus-visible:ring-purple-600'
                  }`}
                >
                  {isLoadingAudio ? (
                    <AudioLoadingAnimation />
                  ) : isPlaying ? (
                    <>
                      <Pause className="w-5 h-5" />
                      <span>Pause Story</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      <span>Listen to Story</span>
                    </>
                  )}
                  <Volume2 className="w-5 h-5" />
                </button>
              </div>
              
              {/* Highlighting Toggle */}
              {(audioUrl || isPlaying) && (
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-secondary/10 hover:bg-secondary/20 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md">
                    <input
                      type="checkbox"
                      checked={highlightingEnabled}
                      onChange={(e) => {
                        setHighlightingEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setCurrentWordIndex(-1);
                        }
                      }}
                      className="w-4 h-4 text-primary rounded focus:ring-primary"
                    />
                    <span className="text-sm text-gray-800 font-medium">Highlight words as they're read</span>
                  </label>
                </div>
              )}
              
              {/* Sound Effects Toggle */}
              {(audioUrl || isPlaying) && (
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-accent/10 hover:bg-accent/20 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md">
                    <input
                      type="checkbox"
                      checked={soundEffectsEnabled}
                      onChange={(e) => {
                        setSoundEffectsEnabled(e.target.checked);
                      }}
                      className="w-4 h-4 text-accent rounded focus:ring-accent"
                    />
                    <Music className="w-4 h-4 text-accent" />
                    <span className="text-sm text-gray-800 font-medium">Play whimsical sound effects</span>
                  </label>
                </div>
              )}
              
              {/* Voice Assignment Button */}
              <div className="flex justify-center">
                <Button
                  onClick={() => {
                    setSelectedStoryForVoice(selectedStory);
                    setVoiceAssignmentOpen(true);
                  }}
                  variant="outline"
                  className="bg-gradient-to-r from-purple-50 to-teal-50 hover:from-purple-100 hover:to-teal-100 border-purple-200 relative z-10"
                  data-testid="button-voice-settings"
                >
                  <Volume2 className="w-4 h-4 mr-2" />
                  Voice Settings
                </Button>
              </div>
              
              {/* Multi-Voice Audio Player */}
              {selectedStory.characterMap && selectedStory.characterMap.narrator && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Volume2 className="w-5 h-5 text-purple-600" />
                    Multi-Voice Audio Player
                  </h3>
                  <SegmentedAudioPlayer
                    storyId={selectedStory.id}
                    storyContent={selectedStory.content}
                    hasVoiceAssignments={!!selectedStory.characterMap?.narrator}
                  />
                </div>
              )}
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="bg-gradient-to-r from-purple-50 to-teal-50 rounded-xl p-6 border border-purple-100">
                {highlightingEnabled && (isPlaying || currentWordIndex >= 0) ? (
                  renderHighlightedStory(selectedStory.content)
                ) : (
                  <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        img: ({ node, ...props }) => (
                          <img
                            {...props}
                            className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg"
                            style={{ display: 'block' }}
                          />
                        ),
                        p: ({ node, ...props }) => (
                          <p {...props} className="text-primary leading-relaxed text-lg mb-4" />
                        )
                      }}
                    >
                      {selectedStory.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="font-semibold text-primary mb-2 flex items-center gap-2">
                <TeddyBear className="w-4 h-4 text-secondary" />
                Story Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-secondary">Interests:</span>
                  <span className="ml-2 text-primary">{selectedStory.interests.join(', ')}</span>
                </div>
                <div>
                  <span className="font-medium text-secondary">Lesson:</span>
                  <span className="ml-2 text-primary">{selectedStory.lesson}</span>
                </div>
              </div>
            </div>
            
            {/* Share Story Buttons */}
            <div className="mt-8 flex justify-center gap-3">
              <ShareStory story={selectedStory} />
              <CommunityShareButton 
                storyId={selectedStory.id} 
                isOwner={user?.id === selectedStory.userId} 
              />
            </div>
            
            {/* PDF Book Downloads */}
            <div className="mt-8">
              <PdfBookDownload 
                storyId={selectedStory.id} 
                storyTitle={selectedStory.title || 'My Bedtime Story'} 
              />
            </div>
            
            {/* Print Book Order */}
            <div className="mt-8">
              <PrintBookOrder storyId={selectedStory.id} storyTitle={selectedStory.title || undefined} />
            </div>
            
            {/* Sticker Generator */}
            <StickerGenerator storyId={selectedStory.id} storyTitle={selectedStory.title || undefined} />
            
            {/* Merchandise Catalog */}
            <MerchandiseCatalog storyId={selectedStory.id} storyTitle={selectedStory.title || undefined} />
          </div>
          </div>
        </StoryLibraryErrorBoundary>
        </div>
      </div>
    );
  }

  // Check if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-4">
          <div className="text-center py-16">
            <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-secondary mb-2">Please Log In to View Your Stories</h3>
            <p className="text-secondary mb-6">You need to be logged in to access your story library.</p>
            <Link href="/login">
              <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 rounded-lg hover:from-purple-700 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg">
                Log In
              </button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50">
      <Navigation />
      <StoryLibraryErrorBoundary>
        <div className="max-w-6xl mx-auto p-4">

        {/* Page Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src={getLogoSrc()} alt="SnuggleCrafters Logo" className="w-10 h-10 object-contain" />
            <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent">Story Library</h2>
          </div>
          <p className="text-gray-700">Your collection of magical bedtime stories</p>
        </div>

        {/* Search and Filter Controls - Always visible for better UX */}
        <div className="mb-8 space-y-4">
          {/* Search Component */}
          <StorySearch 
            onSearch={handleSearchFiltersChange}
            totalResults={filteredStories.length}
            isSearching={isFetching}
            initialFilters={searchFilters}
          />
          
          {/* Favorites Toggle */}
          <div className="flex justify-center">
            <Button
              variant="gradient"
              size="sm"
              onClick={handleFavoritesToggle}
              className={filterFavorites ? "opacity-100" : "opacity-80 hover:opacity-100"}
              title={filterFavorites ? "Show all stories" : "Show only favorites"}
              data-testid="button-favorites-toggle"
            >
              <Heart className={`w-4 h-4 mr-2 ${filterFavorites ? 'fill-current' : ''}`} />
              Favorites Only
            </Button>
          </div>
        </div>

        {/* View Mode Toggle and Create Playlist Button */}
        {filteredStories && filteredStories.length > 0 && (
          <div className="flex justify-center items-center gap-4 mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-lg shadow-card p-1 inline-flex">
              <button
                onClick={() => handleViewModeChange('bookshelf')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'bookshelf' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid="button-view-bookshelf"
              >
                <BookOpen className="w-4 h-4" />
                <span>Bookshelf</span>
              </button>
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid="button-view-grid"
              >
                <Grid3X3 className="w-4 h-4" />
                <span>Grid</span>
              </button>
            </div>
            
            <Dialog open={createPlaylistOpen} onOpenChange={setCreatePlaylistOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-white dark:bg-gray-800"
                  data-testid="button-create-playlist"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Playlist
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="dialog-create-playlist">
                <DialogHeader>
                  <DialogTitle>Create New Playlist</DialogTitle>
                  <DialogDescription>
                    Create a playlist to organize your favorite stories
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="playlist-name">Name</Label>
                    <Input
                      id="playlist-name"
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      placeholder="My Favorite Stories"
                      data-testid="input-playlist-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="playlist-description">Description (Optional)</Label>
                    <Textarea
                      id="playlist-description"
                      value={newPlaylistDescription}
                      onChange={(e) => setNewPlaylistDescription(e.target.value)}
                      placeholder="A collection of my favorite bedtime stories"
                      data-testid="textarea-playlist-description"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreatePlaylistOpen(false);
                      setNewPlaylistName('');
                      setNewPlaylistDescription('');
                    }}
                    data-testid="button-cancel-create-playlist"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (newPlaylistName.trim()) {
                        createPlaylistMutation.mutate({
                          name: newPlaylistName,
                          description: newPlaylistDescription || undefined
                        });
                      }
                    }}
                    disabled={!newPlaylistName.trim() || createPlaylistMutation.isPending}
                    data-testid="button-confirm-create-playlist"
                  >
                    {createPlaylistMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Playlist'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {isLoading || isFetching ? (
          <div className="space-y-6">
            {/* Loading skeleton for search results */}
            {isFetching && !isLoading && (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-2" />
                <p className="text-sm text-gray-600">Searching stories...</p>
              </div>
            )}
            
            {/* Story grid skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-surface rounded-xl shadow-card p-6 animate-pulse" data-testid="skeleton-story-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                  <div className="h-5 bg-gray-200 rounded mb-3 w-3/4"></div>
                  <div className="space-y-2 mb-4">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : filteredStories && filteredStories.length > 0 ? (
          viewMode === 'bookshelf' ? (
            <AnimatedBookshelf 
              stories={filteredStories} 
              onBookSelect={(story) => {
                setSelectedStory(story);
                setCurrentWordIndex(-1);
                setLastSoundIndex(-10);
                
                // Detect story moments for sound effects
                const moments = detectStoryMoments(story.content);
                setStoryMoments(moments);
                console.log('Detected story moments:', moments);
              }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStories.map((story) => (
                <div
                  key={story.id}
                  className="relative"
                >
                  {/* Favorite Heart Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavoriteMutation.mutate(story.id);
                    }}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gradient-to-r from-purple-600/80 to-teal-600/80 backdrop-blur-sm hover:from-purple-600 hover:to-teal-600 transition-all"
                  >
                    <Heart 
                      className={`w-5 h-5 ${favoriteIds.includes(story.id) ? 'fill-white text-white' : 'text-white/70'}`}
                    />
                  </button>
                  
                  {/* Share Button */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: story.title || 'My Bedtime Story',
                            text: `Check out this amazing bedtime story: ${story.title || 'My Story'}`,
                            url: `${window.location.origin}/story/${story.id}`
                          });
                        } catch (err) {
                          console.log('Share cancelled or failed:', err);
                        }
                      } else {
                        // Fallback to copying URL
                        navigator.clipboard.writeText(`${window.location.origin}/story/${story.id}`);
                        toast({
                          title: "Link copied!",
                          description: "Story link has been copied to your clipboard",
                        });
                      }
                    }}
                    className="absolute top-4 right-16 z-10 p-2 rounded-full bg-gradient-to-r from-teal-600/80 to-purple-600/80 backdrop-blur-sm hover:from-teal-600 hover:to-purple-600 transition-all"
                  >
                    <Share2 className="w-5 h-5 text-purple-50" />
                  </button>
                  
                  {/* Add to Playlist Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-4 right-28 z-10 p-2 rounded-full bg-gradient-to-r from-amber-600/80 to-orange-600/80 backdrop-blur-sm hover:from-amber-600 hover:to-orange-600 transition-all"
                        data-testid={`button-add-to-playlist-${story.id}`}
                      >
                        <ListPlus className="w-5 h-5 text-purple-50" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
                      {playlists && playlists.length > 0 ? (
                        playlists.map((playlist: any) => (
                          <DropdownMenuItem
                            key={playlist.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToPlaylistMutation.mutate({
                                playlistId: playlist.id,
                                storyId: story.id
                              });
                            }}
                            data-testid={`menuitem-playlist-${playlist.id}`}
                          >
                            <ListPlus className="h-4 w-4 mr-2" />
                            {playlist.name}
                          </DropdownMenuItem>
                        ))
                      ) : (
                        <DropdownMenuItem
                          disabled
                          data-testid="menuitem-no-playlists"
                        >
                          No playlists yet
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <div
                    onClick={() => {
                      setSelectedStory(story);
                      setCurrentWordIndex(-1);
                      setLastSoundIndex(-10);
                      
                      // Detect story moments for sound effects
                      const moments = detectStoryMoments(story.content);
                      setStoryMoments(moments);
                      console.log('Detected story moments:', moments);
                    }}
                    className="bg-surface rounded-xl shadow-card p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                  >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Book className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium text-secondary">
                      Age {story.age} • {story.gender}
                    </span>
                  </div>
                  <span className="capitalize px-2 py-1 bg-accent/10 text-secondary rounded-full text-xs">
                    {story.style}
                  </span>
                </div>
                
                {/* Share Story Button */}
                <div className="flex gap-2 mb-3">
                  <ShareStory story={story} />
                  <CommunityShareButton 
                    storyId={story.id} 
                    isOwner={user?.id === story.userId} 
                  />
                </div>

                {story.title && (
                  <h3 className="text-lg font-semibold text-primary mb-2 line-clamp-2">
                    {story.title}
                  </h3>
                )}
                
                <div className="mb-4">
                  <p className="text-primary line-clamp-3 text-sm leading-relaxed">
                    {story.content.substring(0, 150)}...
                  </p>
                </div>

                <div className="flex items-center justify-between text-xs text-secondary">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(story.createdAt).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {Math.ceil(story.content.split(' ').length / 200)} min read
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-secondary">
                    <span className="font-medium">Interests:</span> {story.interests.slice(0, 2).join(', ')}
                    {story.interests.length > 2 && ` +${story.interests.length - 2} more`}
                  </div>
                </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-16" data-testid="text-empty-state">
            <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-secondary mb-2">
              {stories && stories.length > 0 
                ? (filterFavorites 
                    ? 'No favorites found' 
                    : 'No stories match your filters')
                : 'No stories yet'}
            </h3>
            <p className="text-secondary mb-6">
              {stories && stories.length > 0 
                ? (filterFavorites 
                    ? 'Switch off "Favorites Only" to see all stories or add some stories to your favorites first.'
                    : 'Try adjusting your filters to see more stories')
                : 'Create your first magical bedtime story!'}
            </p>
            <Link href="/home">
              <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 rounded-lg hover:from-purple-700 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg" data-testid="button-create-story">
                Create Your First Story
              </button>
            </Link>
          </div>
        )}
        </div>
      </StoryLibraryErrorBoundary>
      
      {/* Voice Assignment Dialog */}
      <VoiceAssignmentDialog
        open={voiceAssignmentOpen && !!selectedStoryForVoice}
        onOpenChange={(open) => {
          setVoiceAssignmentOpen(open);
          if (!open) {
            setSelectedStoryForVoice(null);
          }
        }}
        storyId={selectedStoryForVoice?.id || 0}
        storyContent={selectedStoryForVoice?.content || ''}
        currentCharacterMap={selectedStoryForVoice?.characterMap || undefined}
      />
      
      <Footer />
    </div>
  );
}